import type {
  ExternalPostSummary,
  PostMedia,
  PostOrigin,
  PostPlatform,
  PostSummaryTarget,
} from '../../types/publishing.js';

// Publishing — natively-published ("external") posts on the calendar (2026-07-20).
//
// Pure: no I/O, no Date.now. The server fetches raw rows from Zernio's
// `listPosts({ source: 'external' })`; everything about how a row is TAGGED,
// NORMALIZED and FILTERED lives here so it can be unit-tested off fixtures.
//
// ═══ PROVENANCE OF THESE SHAPES ═══
// Field names come from the live probe run recorded in the brief
// (_architect/briefs/2026-07-20-calendar-external-posts-and-post-stats.md):
// `content`, `mediaItems[].thumbnail`, `platforms[].publishedAt`,
// `platforms[].platformPostUrl`, and the undocumented `origin`. Several of those
// are ABSENT from the SDK's `Post` type, which is precisely why they can only have
// come from an observed payload.
//
// ⚠️ WHAT IS *NOT* PINNED DOWN: the probe's verbatim row captures were never
// written into the findings note, so while the field NAMES are observed, the
// VALUE shapes (date encoding, media-item shape, null-vs-absent) are not. Every
// reader below is therefore deliberately tolerant — see toIso / toMedia. Do not
// "tighten" these into exact-shape assumptions without a real captured payload;
// this integration has shipped two live bugs from trusting a shape over a payload.

// ---------------------------------------------------------------------------
// origin tagging — the safety boundary
// ---------------------------------------------------------------------------

/** Which `listPosts({source})` collection a row was fetched from. */
export type QueriedSource = 'zernio' | 'external';

export interface OriginSignals {
  /** The row's own `origin` field. Undocumented — never the sole signal. */
  rowOrigin?: unknown;
  /** Which query returned the row. Independent of anything in the payload. */
  queriedSource?: QueriedSource;
}

/**
 * Resolve a post's origin from TWO independent signals — belt-and-braces,
 * because `origin` is undocumented and could vanish in any Zernio release
 * without notice.
 *
 * THE RULE, and the direction matters: anything that is not POSITIVELY
 * identified as ours resolves to 'external' (read-only). Being wrong in that
 * direction means a post we own renders as un-draggable — visible, annoying,
 * instantly reported. Being wrong the other way means the calendar hands an AM a
 * drag handle on a post the agency does not own and cannot move, and the failure
 * only surfaces as a confusing provider error after the fact. So a missing
 * `origin` on a row of unknown provenance is treated as external, not as ours.
 *
 * 'kyck' therefore requires BOTH signals to agree, or the authoritative one (the
 * query) to say so with nothing contradicting it.
 */
export function resolvePostOrigin(signals: OriginSignals): PostOrigin {
  const rowOrigin =
    typeof signals.rowOrigin === 'string' ? signals.rowOrigin.trim().toLowerCase() : null;

  // The row explicitly says external — believe it, whatever the query was.
  if (rowOrigin === 'external') return 'external';

  // The query is authoritative for the external direction: rows returned by the
  // external collection ARE external, even if the field is missing or renamed.
  if (signals.queriedSource === 'external') return 'external';

  // Ours only when the query says so and the row doesn't disagree.
  if (signals.queriedSource === 'zernio') return 'kyck';

  // No usable signal → fail safe.
  return 'external';
}

// ---------------------------------------------------------------------------
// tolerant readers (see the PROVENANCE note above)
// ---------------------------------------------------------------------------

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

// ⚠️ OBSERVED 2026-07-20: `analytics.lastUpdated` arrives SPACE-SEPARATED with no
// timezone — "2026-07-20 08:22:24" — while every other timestamp in the same
// payload is ISO with a Z. This regex is what tells the two apart.
const SPACE_SEPARATED_NAIVE = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/;

/**
 * Coerce an observed timestamp to ISO, accepting the encodings this API actually
 * emits: ISO-with-Z, the space-separated naive form, or epoch seconds/millis.
 * Anything unparseable → null (the row still renders; it just won't bucket).
 *
 * ⚠️⚠️ THE SPACE-SEPARATED CASE IS NOT COSMETIC — DO NOT "SIMPLIFY" IT AWAY.
 * `Date.parse("2026-07-20 08:22:24")` treats a naive datetime as LOCAL time. In
 * Brisbane (UTC+10) that lands the instant 10 hours early, so a stats snapshot
 * taken seconds ago renders "as of 10h ago" — and in a NEGATIVE-offset timezone
 * it lands in the future, where formatStatsAsOf's clamp turns genuinely stale
 * figures into "just now". That second direction is the dangerous one: the whole
 * point of the as-of line is to stop stale numbers reading as current.
 *
 * WE TREAT IT AS UTC, on evidence rather than convention: in the observed payload
 * `lastUpdated` was "2026-07-20 08:22:24" while the same post's `updatedAt` was
 * "2026-07-20T08:22:27.221Z" — THREE SECONDS apart on the same clock. A 10-hour
 * local-time reading would put them 10 hours apart, which is not a thing two
 * fields written by one sync pass do. If this inference is ever wrong the cost is
 * a fixed offset in a human-readable "as of" label, not corrupted data.
 */
export function toIso(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Heuristic: 10-digit values are epoch SECONDS, 13-digit are millis. Anything
    // before ~1973 in millis would be absurd for a social post, so the split is safe.
    const ms = value < 1e11 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  const s = str(value);
  if (!s) return null;

  // Naive "YYYY-MM-DD HH:mm:ss" → pin to UTC before parsing. Only rewritten when
  // there is NO timezone present, so an offset-carrying string is left alone.
  const naive = SPACE_SEPARATED_NAIVE.exec(s);
  const normalized = naive ? `${naive[1]}T${naive[2]}Z` : s;

  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

const KNOWN_PLATFORMS: PostPlatform[] = ['facebook', 'instagram', 'googlebusiness'];

/**
 * Map a Zernio platform token to ours. Unknown tokens (linkedin, tiktok, youtube
 * — real Zernio platforms we don't publish to) return null and the entry is
 * SKIPPED rather than coerced: rendering a LinkedIn post on a calendar that can't
 * post to LinkedIn would be a lie about our own coverage.
 */
export function toPostPlatform(value: unknown): PostPlatform | null {
  const s = str(value)?.toLowerCase();
  if (!s) return null;
  return (KNOWN_PLATFORMS as string[]).includes(s) ? (s as PostPlatform) : null;
}

/**
 * Normalize one observed `mediaItems[]` entry. Tolerant of a bare url string as
 * well as the documented object — media is ADDITIVE CONTEXT on the calendar
 * (a hover thumbnail), never load-bearing, so a shape surprise must degrade to
 * "no media", never throw and cost the whole post its calendar row.
 */
function toMedia(raw: unknown): PostMedia | null {
  if (typeof raw === 'string') {
    const url = str(raw);
    return url ? { type: 'image', url, source: 'external', thumbnailUrl: null } : null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Record<string, unknown>;
  const url = str(item.url) ?? str(item.thumbnail) ?? str(item.thumbnailUrl);
  if (!url) return null;
  const type = str(item.type)?.toLowerCase() === 'video' ? 'video' : 'image';
  return {
    type,
    url,
    source: 'external',
    thumbnailUrl: str(item.thumbnail) ?? str(item.thumbnailUrl) ?? null,
    altText: str(item.altText),
  };
}

// ---------------------------------------------------------------------------
// row → ExternalPostSummary
// ---------------------------------------------------------------------------

export interface ExternalNormalizeContext {
  /** The kyck account we fetched this row on behalf of. */
  kyckAccountId: string;
  /** Which collection the query read — the independent origin signal. */
  queriedSource: QueriedSource;
}

/**
 * Normalize one observed external row into a calendar-ready summary.
 *
 * Returns null (row DROPPED) when the post can't be placed honestly:
 *  - no id (nothing to key a stats lookup on),
 *  - no platform we support (see toPostPlatform),
 *  - the row resolves to 'kyck' — a caller asked for external and got one of
 *    ours back; it belongs to the mirror, not here, and duplicating it onto the
 *    calendar would double-count the day.
 *
 * A dropped row is invisible, which for READ-ONLY context is the right failure:
 * the alternative is a half-formed chip an AM can't click, link or interpret.
 */
export function normalizeExternalPost(
  raw: unknown,
  ctx: ExternalNormalizeContext,
): ExternalPostSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;

  const id = str(row._id) ?? str(row.id);
  if (!id) return null;

  if (resolvePostOrigin({ rowOrigin: row.origin, queriedSource: ctx.queriedSource }) !== 'external') {
    return null;
  }

  // One target per platform entry we recognise. This mirrors the kyck fan-out:
  // the calendar renders one ROW per target, so a post that went to both FB and
  // IG natively shows as two lines, exactly like one of ours.
  const rawTargets = Array.isArray(row.platforms) ? row.platforms : [];
  const targets: PostSummaryTarget[] = [];
  const platforms: PostPlatform[] = [];
  let permalink: string | null = null;
  let publishedAt: string | null = null;

  for (const entry of rawTargets) {
    if (!entry || typeof entry !== 'object') continue;
    const t = entry as Record<string, unknown>;
    const platform = toPostPlatform(t.platform);
    if (!platform) continue;
    const at = toIso(t.publishedAt) ?? toIso(t.scheduledFor);
    targets.push({ platform, scheduledFor: at });
    platforms.push(platform);
    permalink ??= str(t.platformPostUrl);
    // Earliest publish across destinations anchors the post.
    if (at && (!publishedAt || at < publishedAt)) publishedAt = at;
  }

  if (targets.length === 0) return null;

  // Post-level fallbacks for a row whose per-platform timestamps were all absent.
  publishedAt ??= toIso(row.publishedAt) ?? toIso(row.scheduledFor) ?? toIso(row.createdAt);

  const media = (Array.isArray(row.mediaItems) ? row.mediaItems : [])
    .map(toMedia)
    .filter((m): m is PostMedia => m !== null);

  return {
    id,
    kyckAccountId: ctx.kyckAccountId,
    origin: 'external',
    // Observed: every external row came back 'published' (the probe found the
    // status filter is ignored on this collection and future-dated rows don't
    // exist). We do NOT trust the field blindly — anything else is still shown as
    // published, because by definition this collection is what already went out.
    status: 'published',
    content: str(row.content) ?? '',
    media,
    platforms,
    targets,
    // The calendar has ONE date axis; an external post's date is when it published.
    scheduledFor: publishedAt,
    publishedAt,
    permalink,
  };
}

/** Normalize a page of rows, dropping the ones that can't be placed. */
export function normalizeExternalPosts(
  rows: unknown[],
  ctx: ExternalNormalizeContext,
): ExternalPostSummary[] {
  return rows
    .map((r) => normalizeExternalPost(r, ctx))
    .filter((p): p is ExternalPostSummary => p !== null);
}

// ---------------------------------------------------------------------------
// local date filtering — because dateFrom is IGNORED server-side
// ---------------------------------------------------------------------------

/**
 * ⚠️ `dateFrom` DOES NOT WORK on this endpoint. The probe passed YYYY-MM-DD, full
 * ISO, epoch millis, and omitted it entirely — all four returned the IDENTICAL
 * total. There is no server-side date filtering to lean on, so the range has to
 * be applied here, after fetching.
 *
 * That is the whole reason the fetch is paginated-and-capped rather than
 * range-scoped: we cannot ask for less, so we bound what we take.
 */
export function filterByDateRange<T extends { scheduledFor: string | null }>(
  posts: T[],
  fromIso: string,
  toIso_: string,
): T[] {
  return posts.filter((p) => {
    if (!p.scheduledFor) return false;
    return p.scheduledFor >= fromIso && p.scheduledFor <= toIso_;
  });
}

/**
 * The inclusive ISO range covering a month grid — the visible 6×7 cells, which
 * spill into the neighbouring months, plus a day of slack at each end so a post
 * near a boundary can't be filtered out by timezone drift.
 */
export function monthGridRange(year: number, month0: number): { from: string; to: string } {
  const first = new Date(year, month0, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay() - 1); // grid start, minus slack
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6 * 7 + 2); // 6 weeks + slack
  end.setHours(23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
}

// ---------------------------------------------------------------------------
// pagination bounds
// ---------------------------------------------------------------------------

// Observed: `limit` caps at 100/page, and one account returned 165 rows across 2
// pages. 6 pages = a 600-row ceiling per platform, comfortably past the 300–400
// days of history the probe found, so the cap should never bite in practice —
// it exists so a pathological account can't stall the calendar indefinitely.
export const EXTERNAL_PAGE_LIMIT = 100;
export const EXTERNAL_MAX_PAGES = 6;

export interface PageCursor {
  page: number;
  fetched: number;
}

/**
 * Should the fetcher ask for another page? Stops on a short page (the natural
 * end), on the page cap, or on a page that returned nothing.
 *
 * `capped` distinguishes "we reached the end of the data" from "we stopped early"
 * — the caller surfaces the difference rather than presenting a truncated history
 * as complete. A silent cap reads as "this account posted nothing before May",
 * which is exactly the kind of quiet wrongness that gets planned around.
 */
export function nextPage(
  cursor: PageCursor,
  lastPageRows: number,
): { done: boolean; capped: boolean; next: PageCursor } {
  const fetched = cursor.fetched + lastPageRows;
  if (lastPageRows < EXTERNAL_PAGE_LIMIT) {
    return { done: true, capped: false, next: { page: cursor.page, fetched } };
  }
  if (cursor.page >= EXTERNAL_MAX_PAGES) {
    return { done: true, capped: true, next: { page: cursor.page, fetched } };
  }
  return { done: false, capped: false, next: { page: cursor.page + 1, fetched } };
}
